// One people picker for every owner / requested by / stakeholder field.
//
// It stores what the systems need (an email, or whatever free text was typed)
// but SHOWS a name: the field reads "Kyung Han", never "kyung@asknexus.ai",
// once a person is chosen. Typing matches names as well as emails, and typing
// a known person's full name stores their email. Anyone may still be typed
// in; an unknown value is kept and shown exactly as written.

import { useMemo, useState } from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { personName } from '../../services/peopleNames';

export default function PersonField({
    value, onChange, options = [], people = [], multiple = false,
    placeholder, helperText, FormHelperTextProps, sx, id, inputAriaLabel,
}) {
    // A "name" that is itself an email (a superuser login with no display
    // name) tells us nothing: skip it so the shared name map can answer.
    const byEmail = useMemo(() => new Map(people
        .filter((p) => p.name && !String(p.name).includes('@'))
        .map((p) => [String(p.email).toLowerCase(), p.name])), [people]);
    const display = (v) => {
        const s = String(v || '');
        return byEmail.get(s.toLowerCase()) || personName(s);
    };
    const withEmail = (v) => { const n = display(v); return n !== v ? `${n} (${v})` : v; };
    const resolve = (typed) => {
        const t = String(typed || '').trim();
        if (!t || t.includes('@')) return typed || '';
        const hit = [...byEmail.entries()].find(([, n]) => String(n).toLowerCase() === t.toLowerCase())
            || options.map((o) => [o, display(o)]).find(([, n]) => n.toLowerCase() === t.toLowerCase());
        return hit ? hit[0] : typed;
    };
    const filterOptions = (opts, { inputValue }) => {
        const q = String(inputValue || '').toLowerCase().trim();
        return opts.filter((o) => !q || String(o).toLowerCase().includes(q) || display(o).toLowerCase().includes(q));
    };
    const [typing, setTyping] = useState(null);

    const input = (p) => (
        <TextField {...p} id={id} size="small" fullWidth placeholder={placeholder}
            helperText={helperText} FormHelperTextProps={FormHelperTextProps}
            // Not an email or login field: tells browser password/autofill
            // extensions to stay out (one injected its own popup into the
            // stakeholder box and took focus away from the page).
            inputProps={{ ...p.inputProps, autoComplete: 'off', 'data-1p-ignore': 'true', 'data-lpignore': 'true',
                'data-bwignore': 'true', 'data-form-type': 'other', name: 'nexus-person',
                ...(inputAriaLabel ? { 'aria-label': inputAriaLabel } : {}) }} />
    );

    if (multiple) {
        return (
            <Autocomplete
                multiple freeSolo options={options} value={value || []} sx={sx}
                filterOptions={filterOptions}
                getOptionLabel={(o) => display(o)}
                onChange={(_, val) => onChange(val.map((v) => resolve(v)))}
                renderOption={(props, option) => <li {...props} key={option}>{withEmail(option)}</li>}
                renderTags={(val, getTagProps) => val.map((s, i) => (
                    // eslint-disable-next-line react/jsx-key
                    <Chip label={display(s)} title={s} size="small" {...getTagProps({ index: i })} />
                ))}
                renderInput={input}
            />
        );
    }
    const shown = typing !== null ? typing : (value ? display(value) : '');
    return (
        <Autocomplete
            freeSolo options={options} value={value || null} sx={sx}
            filterOptions={filterOptions}
            getOptionLabel={(o) => (typeof o === 'string' ? display(o) : '')}
            inputValue={shown}
            onInputChange={(_, v, reason) => {
                if (reason === 'reset') return;
                setTyping(v);
                onChange(resolve(v));
            }}
            onChange={(_, v) => { setTyping(null); onChange(v ? resolve(String(v)) : ''); }}
            onBlur={() => setTyping(null)}
            renderOption={(props, option) => <li {...props} key={option}>{withEmail(option)}</li>}
            renderInput={input}
        />
    );
}
